// Constants
export const NARRATOR_TOKEN = "{{narr}}";
export const USER_SLOT_TOKEN = "{{user}}";
export const CHARACTER_SLOT_TOKEN = "{{char}}";
export const SPECIAL_TOKENS = new Set([USER_SLOT_TOKEN, CHARACTER_SLOT_TOKEN, NARRATOR_TOKEN]);

export const LINE_LENGTH_LIMIT = 2000; // 2000 instead of 2048 to give some room for user and character tokens
export const TOTAL_LENGTH_LIMIT = 32000;

// TypeScript enums/types
export enum TemplateType {
  CHARACTER = 'character',
  NARRATOR = 'narrator',
  USER_SLOT = 'user-slot',
  CHARACTER_SLOT = 'char-slot',
}

export interface Message {
  templateType: TemplateType;
  characterName?: string;
  content: string;
  isHidden: boolean;
}

export class ValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ValidationError";
  }
}

/**
 * Load and validate the header from HJS file lines.
 * 
 * @param lines List of strings representing file lines
 * @returns Tuple of [remainingLines, hasHeader]
 */
function loadHeader(
  lines: string[]
): [string[], boolean] {
  // Check if file has a header
  if (!lines.length || lines[0] !== '---') {
    return [lines, false];
  }

  // Find the end of header
  const headerEndIndex = lines.slice(1).findIndex(line => line === '---');
  if (headerEndIndex === -1) {
    throw new ValidationError("Invalid HJS file: unclosed YAML header");
  }

  // Skip the header part and return remaining lines
  return [lines.slice(headerEndIndex + 2), true];
}

/**
 * Parse a .hjs file into messages
 * 
 * @param hjsString Content of the .hjs file
 * @param validCharacterNames Set of valid character names to validate against
 * @returns Array of parsed messages if successful, or true if validation passes with no parsing needed
 */
export function parse(
  hjsString: string,
  validCharacterNames: Set<string> = new Set()
): Message[] | true {
  const lines = hjsString
    .trim()
    .split('\n')
    .filter(line => line.trim());

  // If empty file, return true (valid but empty)
  if (lines.length === 0) {
    return true;
  }

  // Parse header (just to validate and get remaining lines)
  let scriptLines: string[];
  
  try {
    [scriptLines] = loadHeader(lines);
  } catch (e) {
    if (e instanceof ValidationError) {
      throw e;
    }
    throw new ValidationError(`Error parsing header: ${(e as Error).message}`);
  }

  // If no script content after header, return true (valid but empty)
  if (scriptLines.length === 0) {
    return true;
  }

  // Track total content length
  let totalContentLength = 0;

  // Parse messages
  const messages: Message[] = [];
  
  for (let lineNumber = 0; lineNumber < scriptLines.length; lineNumber++) {
    const line = scriptLines[lineNumber];
    
    // Check if line is hidden
    const isHidden = line.startsWith('# ');
    const processedLine = isHidden ? line.slice(2) : line;

    // Split by first colon followed by space
    const colonIndex = processedLine.indexOf(': ');
    if (colonIndex === -1) {
      throw new ValidationError(
        `Invalid line format in HJS file at line ${lineNumber + 1}: ${line}`
      );
    }

    const speaker = processedLine.slice(0, colonIndex);
    const content = processedLine.slice(colonIndex + 2);

    // Validate content length
    if (content.length > LINE_LENGTH_LIMIT) {
      throw new ValidationError(
        `Line content exceeds maximum length of ${LINE_LENGTH_LIMIT} characters at line ${lineNumber + 1}`
      );
    }

    totalContentLength += content.length;
    if (totalContentLength > TOTAL_LENGTH_LIMIT) {
      throw new ValidationError(
        `Total content length exceeds maximum of ${TOTAL_LENGTH_LIMIT} characters`
      );
    }

    // Validate speaker
    if (!SPECIAL_TOKENS.has(speaker) && !validCharacterNames.has(speaker)) {
      throw new ValidationError(
        `Invalid speaker '${speaker}' at line ${lineNumber + 1}. ` +
        `Must be one of: ${Array.from(SPECIAL_TOKENS).join(', ')} or a valid character name`
      );
    }

    // Determine template type and character name
    let templateType: TemplateType;
    let characterName: string | undefined;

    if (speaker === NARRATOR_TOKEN) {
      templateType = TemplateType.NARRATOR;
    } else if (speaker === USER_SLOT_TOKEN) {
      templateType = TemplateType.USER_SLOT;
    } else if (speaker === CHARACTER_SLOT_TOKEN) {
      templateType = TemplateType.CHARACTER_SLOT;
    } else {
      templateType = TemplateType.CHARACTER;
      characterName = speaker;
    }

    messages.push({
      templateType,
      characterName,
      content,
      isHidden
    });
  }

  return messages;
}
